import { useState } from "react";
import {
  ArrowRight,
  CircleDollarSign,
  Link2,
  PackageCheck,
  PlugZap,
  ShoppingBag,
} from "lucide-react";
import type { Currency, Sale } from "../domain/types";
import { PlatformBadge } from "../components/PlatformBadge";
import { PageHeader, StatusChip } from "../components/UI";
import { dateTime, money, shortDate } from "../lib/format";
import { useWeSell } from "../state/WeSellProvider";

function totalsByCurrency(
  sales: Sale[],
  value: (sale: Sale) => number,
): Array<[Currency, number]> {
  const totals = new Map<Currency, number>();
  for (const sale of sales) {
    totals.set(sale.currency, (totals.get(sale.currency) ?? 0) + value(sale));
  }
  return Array.from(totals.entries()).toSorted(([a], [b]) =>
    a.localeCompare(b),
  );
}

function CurrencyTotals({ values }: { values: Array<[Currency, number]> }) {
  if (!values.length) return <strong>Sem dados</strong>;
  return (
    <div className="currency-totals">
      {values.map(([currency, total]) => (
        <strong key={currency}>{money(total, currency)}</strong>
      ))}
    </div>
  );
}

export function Dashboard({ navigate }: { navigate: (route: string) => void }) {
  const { data } = useWeSell();
  const [period, setPeriod] = useState<"30" | "all">("30");
  const [openedAt] = useState(() => Date.now());
  if (!data) return null;
  const cutoff = openedAt - 30 * 86_400_000;
  const periodSales = data.sales.filter(
    (sale) => period === "all" || new Date(sale.orderDate).valueOf() >= cutoff,
  );
  const approved = periodSales.filter((sale) => sale.status === "approved");
  const commission = totalsByCurrency(
    approved,
    (sale) => sale.commissionAmount,
  );
  const gross = totalsByCurrency(approved, (sale) => sale.grossAmount);
  const clicks = data.products.reduce(
    (total, product) => total + product.clicks,
    0,
  );
  const latestTimestamp = Math.max(
    0,
    ...data.sales.map((sale) =>
      Math.max(
        new Date(sale.orderDate).valueOf(),
        new Date(sale.importedAt).valueOf(),
      ),
    ),
    ...data.imports.map((batch) => new Date(batch.createdAt).valueOf()),
  );
  const disconnected = data.platforms.filter(
    (platform) => platform.connectionStatus !== "connected",
  );
  const topPromotions = data.promotions
    .map((promotion) => ({
      promotion,
      sales: approved.filter((sale) => sale.promotionId === promotion.id),
    }))
    .toSorted(
      (a, b) =>
        b.sales.length - a.sales.length ||
        b.promotion.clicks - a.promotion.clicks,
    )
    .slice(0, 4);
  const topContents = data.contents
    .map((content) => {
      const promotionIds = new Set(
        data.promotions
          .filter((promotion) => promotion.contentId === content.id)
          .map((promotion) => promotion.id),
      );
      return {
        content,
        sales: approved.filter(
          (sale) => sale.promotionId && promotionIds.has(sale.promotionId),
        ),
      };
    })
    .toSorted((a, b) => b.sales.length - a.sales.length)
    .slice(0, 4);

  return (
    <>
      <PageHeader
        eyebrow="Visão geral"
        title="Seu projeto de afiliações, num lugar só."
        description={`Acompanhe vendas sem misturar moedas. Último dado: ${
          latestTimestamp
            ? dateTime(new Date(latestTimestamp).toISOString())
            : "ainda não há"
        }.`}
        action={
          <div className="button-group">
            <label className="select-field compact-select">
              <span>Período</span>
              <select
                value={period}
                onChange={(event) =>
                  setPeriod(event.target.value as "30" | "all")
                }
              >
                <option value="30">Últimos 30 dias</option>
                <option value="all">Todo o histórico</option>
              </select>
            </label>
            <button
              className="button button--primary"
              onClick={() => navigate("produtos")}
            >
              <ShoppingBag size={18} aria-hidden="true" /> Novo produto
            </button>
          </div>
        }
      />

      {disconnected.length ? (
        <button className="notice-card" onClick={() => navigate("conexoes")}>
          <span className="notice-card__icon">
            <PlugZap size={20} aria-hidden="true" />
          </span>
          <span>
            <strong>
              {disconnected.length}{" "}
              {disconnected.length > 1
                ? "conexões precisam"
                : "conexão precisa"}{" "}
              de atenção
            </strong>
            <small>
              Conecte as plataformas para reduzir atualizações manuais.
            </small>
          </span>
          <ArrowRight size={20} aria-hidden="true" />
        </button>
      ) : null}

      <section className="metrics-grid" aria-label="Resumo de desempenho">
        <article className="metric-card metric-card--hero">
          <span className="metric-card__icon">
            <CircleDollarSign size={22} aria-hidden="true" />
          </span>
          <p>Comissão aprovada</p>
          <CurrencyTotals values={commission} />
          <small>{approved.length} vendas aprovadas no período</small>
        </article>
        <article className="metric-card">
          <span className="metric-card__icon">
            <PackageCheck size={22} aria-hidden="true" />
          </span>
          <p>Vendas atribuídas</p>
          <CurrencyTotals values={gross} />
          <small>Valor bruto separado por moeda</small>
        </article>
        <article className="metric-card">
          <span className="metric-card__icon">
            <Link2 size={22} aria-hidden="true" />
          </span>
          <p>Cliques rastreados</p>
          <strong>{new Intl.NumberFormat("pt-BR").format(clicks)}</strong>
          <small>{data.promotions.length} links de divulgação</small>
        </article>
      </section>

      <section className="dashboard-grid">
        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Plataformas</p>
              <h2>Comissão e conexão</h2>
            </div>
            <button
              className="text-button"
              onClick={() => navigate("plataformas")}
            >
              Ver códigos
            </button>
          </header>
          <div className="platform-list">
            {data.platforms.map((platform) => {
              const products = data.products.filter(
                (product) => product.platformId === platform.id,
              );
              const platformSales = approved.filter(
                (sale) => sale.platformId === platform.id,
              );
              return (
                <div className="platform-row" key={platform.id}>
                  <PlatformBadge platform={platform} />
                  <div className="platform-row__numbers">
                    <span>
                      <strong>{products.length}</strong> produtos
                    </span>
                    <span className="currency-inline">
                      {totalsByCurrency(
                        platformSales,
                        (sale) => sale.commissionAmount,
                      )
                        .map(([currency, total]) => money(total, currency))
                        .join(" + ") || "Sem comissão"}
                    </span>
                  </div>
                  <StatusChip
                    status={
                      platform.connectionStatus === "connected"
                        ? "ok"
                        : "warning"
                    }
                    label={
                      platform.connectionStatus === "connected"
                        ? "Conectada"
                        : "Configurar"
                    }
                  />
                </div>
              );
            })}
          </div>
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Links</p>
              <h2>O que mais converte</h2>
            </div>
            <button className="text-button" onClick={() => navigate("links")}>
              Ver links
            </button>
          </header>
          <div className="ranking-list">
            {topPromotions.map(({ promotion, sales }, index) => {
              const product = data.products.find(
                (item) => item.id === promotion.productId,
              );
              return (
                <div className="ranking-row" key={promotion.id}>
                  <span className="ranking-row__position">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <strong>{product?.name ?? "Produto removido"}</strong>
                    <small>
                      {promotion.trackingId || product?.shortCode} ·{" "}
                      {sales.length} vendas
                    </small>
                  </div>
                  <span className="ranking-row__value">
                    {totalsByCurrency(sales, (sale) => sale.commissionAmount)
                      .map(([currency, total]) => money(total, currency))
                      .join(" + ") || "Sem comissão"}
                  </span>
                </div>
              );
            })}
            {!topPromotions.length ? (
              <p className="panel__empty">
                Crie seu primeiro link de divulgação.
              </p>
            ) : null}
          </div>
        </article>
      </section>

      <section className="dashboard-grid">
        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Conteúdo</p>
              <h2>Posts que geram comissão</h2>
            </div>
            <button
              className="text-button"
              onClick={() => navigate("conteudos")}
            >
              Ver conteúdos
            </button>
          </header>
          <div className="ranking-list">
            {topContents.map(({ content, sales }, index) => (
              <div className="ranking-row" key={content.id}>
                <span className="ranking-row__position">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <strong>{content.title}</strong>
                  <small>
                    {content.channel} · {sales.length} vendas
                  </small>
                </div>
                <span className="ranking-row__value">
                  {totalsByCurrency(sales, (sale) => sale.commissionAmount)
                    .map(([currency, total]) => money(total, currency))
                    .join(" + ") || "Sem comissão"}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <header className="panel__header">
            <div>
              <p className="eyebrow">Atividade recente</p>
              <h2>Últimas vendas</h2>
            </div>
            <button className="text-button" onClick={() => navigate("vendas")}>
              Abrir vendas
            </button>
          </header>
          <div className="responsive-table">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Produto</th>
                  <th>Comissão</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.slice(0, 6).map((sale) => {
                  const product = data.products.find(
                    (item) => item.id === sale.productId,
                  );
                  return (
                    <tr key={sale.id}>
                      <td>{shortDate(sale.orderDate)}</td>
                      <td>{product?.name ?? "Venda sem produto"}</td>
                      <td className="number-cell">
                        {money(sale.commissionAmount, sale.currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      </section>
    </>
  );
}
